import type { RouterOutputs } from "@/trpc";
import { cn } from "@/lib/utils";
import { BillingPricingMode } from "@api/shared-runtime/enums";

export type UsageBillingEvent =
  RouterOutputs["adminUsageBilling"]["getUnitEvents"]["items"][number];

export type UsageBillingSummaryItem =
  RouterOutputs["adminUsageBilling"]["getUnitSummaries"]["items"][number];
export type UsageBillingCategoryLine =
  UsageBillingSummaryItem["summary"]["categories"][number];

export const UNLIMITED_USAGE_LABEL = "Unlimited";
export const ZERO_USAGE_QUANTITY = "0";

const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatUsageBillingCurrency(amount: string): string {
  return CURRENCY_FORMATTER.format(Number(amount));
}

// usedQuantity/quantity arrive as 4dp numeric strings (e.g. "3.0000").
// Round-tripping through Number() drops trailing zeros so whole counts read
// as plain integers instead of "3.0000".
export function formatUsageBillingQuantity(quantity: string): string {
  return String(Number(quantity));
}

const USAGE_EVENT_SOURCE_LABEL = {
  reveal: "Reveal",
  jobPost: "Job post",
  signature: "Signature",
  shift: "Shift",
  manual: "Manual",
  unknown: "Unknown",
} as const;

// Events carry exactly one populated source FK (or none, for hand-recorded
// history); the first match wins.
export function getUsageEventSourceLabel(event: UsageBillingEvent): string {
  if (event.sourceRevealId) {
    return USAGE_EVENT_SOURCE_LABEL.reveal;
  }
  if (event.sourceJobPostId) {
    return USAGE_EVENT_SOURCE_LABEL.jobPost;
  }
  if (event.sourceFormSubmissionFileResultId) {
    return USAGE_EVENT_SOURCE_LABEL.signature;
  }
  if (event.sourceProviderPayableItemId) {
    return USAGE_EVENT_SOURCE_LABEL.shift;
  }
  if (event.recordedByUserId) {
    return USAGE_EVENT_SOURCE_LABEL.manual;
  }
  return USAGE_EVENT_SOURCE_LABEL.unknown;
}

export function isUsageBillingCategoryUnlimited(
  category: UsageBillingCategoryLine,
): boolean {
  if (category.pricingMode === BillingPricingMode.PerUnit) {
    return category.unitAmount !== null && Number(category.unitAmount) === 0;
  }
  if (category.pricingMode === BillingPricingMode.PercentOfBasis) {
    return (
      category.basisPercent !== null && Number(category.basisPercent) === 0
    );
  }
  // Aggregating different paid rates can also yield a null pricing mode. Its
  // numeric charge distinguishes it from an unpriced category, even at $0.
  return category.pricingMode === null && category.lineAmount === null;
}

export function getUsageBillingAllowanceLabel(
  category: UsageBillingCategoryLine,
): string {
  if (isUsageBillingCategoryUnlimited(category)) {
    return UNLIMITED_USAGE_LABEL;
  }
  if (category.pricingMode === BillingPricingMode.PercentOfBasis) {
    return "—";
  }
  if (category.includedQuantity === null) {
    return "—";
  }
  return String(category.includedQuantity);
}

// Voided events read as struck-through everywhere they appear (inline
// preview and the full event list) so an admin scanning either table can
// tell at a glance which rows no longer count toward billing.
export function getUsageBillingEventRowClassName(
  event: UsageBillingEvent,
): string {
  return cn(
    "cursor-pointer hover:bg-gray-100",
    event.voidedAt && "line-through text-gray-400",
  );
}
